/**
 * DAGNodeComponent — Unified custom node for @xyflow/react v12.
 *
 * Renders all 12 DAG NodeTypes with:
 * - Shape/icon/color from nodeTypeConfig (data-driven, no switch)
 * - Criticality border (red glow for LifeCritical, etc.)
 * - Status badge in instance mode
 * - Actor badge when assigned
 * - RuleSet indicator when governed by rules
 * - Connection handles (source/target)
 */

import React, { memo } from 'react';
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import { Badge, Box, Group, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import {
  NODE_TYPE_CONFIG,
  CRITICALITY_CONFIG,
  STATUS_CONFIG,
  type DagNodeType,
  type DagCriticality,
  type DagNodeStatus,
} from '../config/nodeTypeConfig';

// ── Node data interface ──────────────────────────────────────────────

export interface WorkflowDAGNodeData extends Record<string, unknown> {
  label: string;
  nodeType: DagNodeType;
  criticality: DagCriticality;
  status?: DagNodeStatus;
  phaseId?: string;
  phaseName?: string;
  phaseOrder?: number;
  requiredRole?: string;
  timeoutMs?: number;
  assignedActorName?: string;
  ruleSetCount?: number;
  highlighted?: boolean;
  mode: 'definition' | 'instance';
  /** Optional RBAC and backend-sync metadata for role nodes (Issue #846). */
  metadata?: Record<string, unknown>;
}

/** React Flow v12 typed node for DAG nodes. */
export type WorkflowDAGNode = Node<WorkflowDAGNodeData, 'dagNode'>;

// ── Shape clip paths ─────────────────────────────────────────────────

const SHAPE_STYLES: Record<string, React.CSSProperties> = {
  rect: { borderRadius: 4 },
  rounded: { borderRadius: 16 },
  diamond: { borderRadius: 4, transform: 'rotate(45deg)' },
  hexagon: { borderRadius: 8, clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' },
  octagon: { borderRadius: 4, clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' },
  parallelogram: { borderRadius: 4, clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)' },
};

// ── Component ────────────────────────────────────────────────────────

function DAGNodeComponentInner({ data, selected }: NodeProps<WorkflowDAGNode>) {
  const nodeConfig = NODE_TYPE_CONFIG[data.nodeType];
  const critConfig = CRITICALITY_CONFIG[data.criticality];
  const statusConfig = data.status ? STATUS_CONFIG[data.status] : undefined;
  const NodeIcon = nodeConfig?.icon;
  const isDiamond = nodeConfig?.shape === 'diamond';

  if (!nodeConfig) return null;

  const borderWidth = selected ? 3 : 2;
  const bgColor = data.mode === 'instance' && statusConfig
    ? statusConfig.bgColor
    : 'var(--mantine-color-white)';

  return (
    <>
      <Handle type="target" position={Position.Top} />

      <Box
        style={{
          minWidth: isDiamond ? 100 : 180,
          minHeight: isDiamond ? 100 : 60,
          padding: isDiamond ? 24 : '8px 12px',
          background: bgColor,
          border: `${borderWidth}px solid ${critConfig.borderColor}`,
          boxShadow: data.highlighted
            ? `0 0 12px 4px rgba(59, 130, 246, 0.5)`
            : critConfig.pulseAnimation
              ? `0 0 8px 2px ${critConfig.glowColor}`
              : `0 1px 4px rgba(0,0,0,0.08)`,
          cursor: 'grab',
          ...(isDiamond ? {} : SHAPE_STYLES[nodeConfig.shape]),
          ...(selected ? { outline: '2px solid var(--mantine-color-blue-5)', outlineOffset: 2 } : {}),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          ...(isDiamond
            ? {
                ...SHAPE_STYLES.diamond,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
            : {}),
        }}
      >
        <div style={isDiamond ? { transform: 'rotate(-45deg)', textAlign: 'center' } : undefined}>
          {/* Header: icon + label */}
          <Group gap={6} wrap="nowrap" justify="center">
            <ThemeIcon size="sm" variant="light" color={nodeConfig.color} radius="xl">
              <NodeIcon size={14} />
            </ThemeIcon>
            <Text size="xs" fw={600} lineClamp={1} style={{ maxWidth: 120 }}>
              {data.label}
            </Text>
          </Group>

          {/* Type badge */}
          <Group gap={4} mt={4} justify="center">
            <Badge size="xs" variant="dot" color={nodeConfig.color}>
              {nodeConfig.label}
            </Badge>

            {/* Status badge (instance mode) */}
            {data.mode === 'instance' && statusConfig && (
              <Badge size="xs" color={statusConfig.color}>
                {statusConfig.label}
              </Badge>
            )}
          </Group>

          {/* Actor assignment */}
          {data.assignedActorName && (
            <Group gap={4} mt={2} justify="center">
              <Tooltip label={`Assigned: ${data.assignedActorName}`}>
                <Badge size="xs" variant="outline" color="teal" leftSection={<IconUser size={10} />}>
                  {data.assignedActorName}
                </Badge>
              </Tooltip>
            </Group>
          )}

          {/* RuleSet indicator */}
          {(data.ruleSetCount ?? 0) > 0 && (
            <Tooltip label={`${data.ruleSetCount} governing rule(s)`}>
              <Badge size="xs" variant="filled" color="red" mt={2}>
                {data.ruleSetCount} rule{(data.ruleSetCount ?? 0) > 1 ? 's' : ''}
              </Badge>
            </Tooltip>
          )}

          {/* Required role (definition mode) */}
          {data.mode === 'definition' && data.requiredRole && (
            <Text size="xs" c="dimmed" ta="center" mt={2}>
              Role: {data.requiredRole}
            </Text>
          )}
        </div>
      </Box>

      <Handle type="source" position={Position.Bottom} />
    </>
  );
}

export const DAGNodeComponent = memo(DAGNodeComponentInner);
